/* eslint-disable max-len */
import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Grid,
  Header,
  Input,
  Link,
  Select,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import {
  colorBorderDividerDefault,
  colorBorderDividerSecondary,
} from '@cloudscape-design/design-tokens';
import { LineItemTypes, StepTypes } from '@parallel-fluidics/shared/constants';
import { decodePPartId, pPartFromFirestore } from '@parallel-fluidics/shared/models/projects/parts';
import PPartDisplay from 'components/Projects/pPartDisplay';
import useProductVersions from 'features/products/hooks/useProductVersions';
import useSearchProducts from 'features/products/hooks/useSearchProducts';
import {
  collection,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useFormikContext } from 'formik';
import useProjects from 'hooks/use-projects';
import useRealtimeDocs from 'hooks/use-realtimeDocs';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  buildLineItemSteps,
  getProductPartOptions,
  getStepFlowOptions,
} from './lineItemSteps';

const stepOptions = Object.values(StepTypes).map((value) => ({ label: value, value }));

const LINE_ITEM_TYPES_OPTIONS = [
  { label: 'On Demand', value: LineItemTypes.ON_DEMAND },
  { label: 'Product', value: LineItemTypes.PRODUCT },
];

function ProductLineItemFields({ shipmentIndex, idx, disableEdit }) {
  const {
    values: { shipments = [] }, setFieldValue, errors,
  } = useFormikContext();
  const item = shipments[shipmentIndex]?.lineItems[idx] || {};

  const [skuFilter, setSkuFilter] = useState('');
  const { products, isLoading: productsLoading } = useSearchProducts(skuFilter);
  const { data: versions, isLoading: versionsLoading } = useProductVersions(item.productDocId || null);

  // Build Select options for SKU picker
  const productOptions = useMemo(() => products.map((p) => ({
    label: p.sku,
    value: p.id,
    description: p.name,
    tags: [p.activeVersionId ? `active: ${p.activeVersionId}` : 'no active version'],
    product: p,
  })), [products]);

  const selectedProductOption = useMemo(() => {
    if (!item.productDocId || !products.length) return null;
    return productOptions.find((o) => o.value === item.productDocId) || null;
  }, [item.productDocId, productOptions, products.length]);

  // Build Select options for version picker
  const versionOptions = useMemo(() => {
    if (!versions?.length) return [];
    return versions.map((v) => ({
      label: v.id,
      value: v.id,
      description: `$${v.unitPrice} — ${v.leadtimeDays ?? '?'} days — ${v.status}`,
      version: v,
    }));
  }, [versions]);

  const selectedVersionOption = useMemo(() => {
    if (!item.productVersionId || !versionOptions.length) return null;
    return versionOptions.find((o) => o.value === item.productVersionId) || null;
  }, [item.productVersionId, versionOptions]);

  // When a product is selected, auto-select the active version
  const handleProductChange = useCallback(({ detail }) => {
    const { product } = detail.selectedOption;
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].productDocId`, product.id);
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].sku`, product.sku);
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].name`, product.name);
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].productVersionId`, product.activeVersionId || null);
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].unitPrice`, null);
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].leadtimeDays`, null);
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].projectId`, '');
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].projectDocId`, '');
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].pPartId`, '');
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].pPartDocId`, null);
  }, [setFieldValue, shipmentIndex, idx]);

  // When a version is selected, auto-fill pricing fields
  const handleVersionChange = useCallback(({ detail }) => {
    const { version } = detail.selectedOption;
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].productVersionId`, version.id);
  }, [setFieldValue, shipmentIndex, idx]);

  useEffect(() => {
    if (!item.productVersionId || !versions?.length) return;
    const version = versions.find(({ id }) => id === item.productVersionId);
    if (!version) return;

    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].unitPrice`, version.unitPrice);
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].leadtimeDays`, version.leadtimeDays);
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].projectId`, version.projectId || '');
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].projectDocId`, version.projectDocId || '');
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].pPartId`, version.pPartId || '');
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].pPartDocId`, version.pPartDocId || null);
  }, [idx, item.productVersionId, setFieldValue, shipmentIndex, versions]);

  return (
    <Grid gridDefinition={[{ colspan: 5 }, { colspan: 4 }, { colspan: 3 }, { colspan: 12 }, { colspan: 12 }]}>
      <FormField
        label="SKU"
        errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.productDocId}
      >
        <Select
          disabled={disableEdit}
          filteringType="manual"
          statusType={productsLoading ? 'loading' : 'finished'}
          loadingText="Loading products…"
          onLoadItems={({ detail }) => setSkuFilter(detail.filteringText)}
          selectedOption={selectedProductOption}
          onChange={handleProductChange}
          options={productOptions}
          placeholder="Search by SKU…"
          empty="No active products found"
        />
      </FormField>

      <FormField
        label="Version"
        errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.productVersionId}
      >
        <Select
          disabled={disableEdit || !item.productDocId}
          statusType={versionsLoading ? 'loading' : 'finished'}
          loadingText="Loading versions…"
          selectedOption={selectedVersionOption}
          onChange={handleVersionChange}
          options={versionOptions}
          empty="Select a SKU first"
        />
      </FormField>

      <FormField
        label="Quantity"
        errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.quantity}
      >
        <Input
          name={`shipments[${shipmentIndex}].lineItems[${idx}].quantity`}
          value={shipments[shipmentIndex]?.lineItems[idx]?.quantity}
          inputMode="numeric"
          onChange={({ detail }) => {
            setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].quantity`, parseInt(detail.value, 10) || null);
          }}
        />
      </FormField>

      {/* Read-only auto-filled info */}
      {item.productVersionId ? (
        <Box color="text-status-inactive" fontSize="body-s">
          {[
            item.unitPrice != null && `Unit price: $${item.unitPrice}`,
            item.leadtimeDays != null && `Lead time: ${item.leadtimeDays} days`,
            item.pPartId && item.projectId && `Part: ${item.projectId}-${item.pPartId}`,
          ].filter(Boolean).join(' · ')}
        </Box>
      ) : null}

      <FormField
        stretch
        label="Notes"
        errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.notes}
      >
        <Textarea
          name={`shipments[${shipmentIndex}].lineItems[${idx}].notes`}
          value={shipments[shipmentIndex]?.lineItems[idx]?.notes}
          onChange={({ detail }) => { setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].notes`, detail.value); }}
        />
      </FormField>
    </Grid>
  );
}

ProductLineItemFields.propTypes = {
  shipmentIndex: PropTypes.number.isRequired,
  idx: PropTypes.number.isRequired,
  disableEdit: PropTypes.bool.isRequired,
};

// ── On-demand line item sub-component ────────────────────────────────────────

function OnDemandLineItemFields({ shipmentIndex, idx, disableEdit }) {
  const [searchParams] = useSearchParams();
  const {
    values: { shipments = [], isInternal }, setFieldValue, errors,
  } = useFormikContext();
  const currentLineItems = shipments[shipmentIndex]?.lineItems || [];
  const item = currentLineItems[idx] || {};

  const { projectOptions } = useProjects({ isInternal });
  const [projectDocId, setProjectDocId] = useState(item?.projectDocId || null);

  const selectedProject = useMemo(() => {
    if (!item?.projectId || !projectOptions?.length) return null;
    return projectOptions.find((option) => option.value === item.projectId);
  }, [projectOptions, item?.projectId]);

  const partsQuery = useMemo(() => {
    if (!projectDocId) return null;
    return query(
      collection(getFirestore(), 'projects', projectDocId, 'parts'),
      orderBy('partId', 'desc'),
    );
  }, [projectDocId]);
  const { data: parts } = useRealtimeDocs({ query: partsQuery, transformData: pPartFromFirestore });

  const edgesQuery = useMemo(() => {
    if (!projectDocId) return null;
    return query(
      collection(getFirestore(), 'projects', projectDocId, 'edges'),
    );
  }, [projectDocId]);
  const { data: edges } = useRealtimeDocs({ query: edgesQuery });

  const { products: partProducts } = useMemo(() => {
    const products = getProductPartOptions(parts);
    return { products };
  }, [parts]);

  const stepFlowOptions = useMemo(() => getStepFlowOptions(parts, edges), [edges, parts]);

  const getSelectedPPart = useCallback((pPartId) => {
    if (!pPartId || !partProducts?.length) return null;
    return partProducts.find((option) => option.value === pPartId);
  }, [partProducts]);

  const getPPartMetadataMessage = useCallback((pPartId) => {
    if (!pPartId || !partProducts?.length) return null;
    const selectedPart = partProducts.find((option) => option.value === pPartId);
    if (!selectedPart || !selectedPart.metadataArr?.length) return null;
    const { metadataArr } = selectedPart;
    const list = metadataArr.map(({ key, value }) => (
      <li key={key}>{`${key}: ${value}`}</li>
    ));
    return (
      <ul>{list}</ul>
    );
  }, [partProducts]);

  useEffect(() => {
    if (!item?.pPartId) return;
    const { material } = decodePPartId(item.pPartId);
    if (!material) return;
    const desc = [material, item.bonding].filter(Boolean).join(' | ');
    setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].desc`, desc);
  }, [item?.pPartId, item?.bonding, setFieldValue, shipmentIndex, idx]);

  return (
    <SpaceBetween size="s">
      <Grid
        gridDefinition={[{ colspan: 6 }, { colspan: 3 }, { colspan: 3 }, { colspan: 6 }, { colspan: 6 }, { colspan: 12 }]}
      >
        <FormField
          label={(
            <span>
              Project ID
              {selectedProject?.partId && (
                <span>
                  {' - '}
                  <Link variant="info" external href={`/part/${selectedProject.partId}`}><i>Check the original quote</i></Link>
                </span>
              )}
            </span>
          )}
          constraintText={(
            <span>
              {'Add a project '}
              <a href={`/projects?${searchParams.toString()}`} target="_blank" rel="noreferrer">here</a>
              .
            </span>
          )}
          errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.projectId}
        >
          <Select
            disabled={disableEdit}
            filteringType="auto"
            selectedOption={selectedProject}
            onChange={({ detail }) => {
              setProjectDocId(detail.selectedOption.id); // set project doc Id to fetch parts
              setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].projectId`, detail.selectedOption.value);
              setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].projectDocId`, detail.selectedOption.id);
              setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].name`, detail.selectedOption.display);
              setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].pPartId`, null);
            }}
            options={projectOptions}
          />
        </FormField>
        <FormField
          label="Part ID"
          constraintText={
            !!projectDocId && (
              <span>
                {'Add a part '}
                <a href={`/projects/${projectDocId}?${searchParams.toString()}`} target="_blank" rel="noreferrer">here</a>
                .
              </span>
            )
          }
          errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.pPartId}
        >
          <Select
            disabled={disableEdit}
            filteringType="auto"
            selectedOption={getSelectedPPart(item.pPartId)}
            onChange={({ detail }) => {
              if (item.pPartId === detail.selectedOption.pPartId) return; // no change
              setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].pPartId`, detail.selectedOption.pPartId);
              const autoSteps = buildLineItemSteps({
                parts,
                edges,
                pPartId: detail.selectedOption.pPartId,
                quantity: item.quantity,
              });
              if (autoSteps.length > 0) {
                setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps`, autoSteps);
              }
            }}
            options={stepFlowOptions}
            empty="No options, select a project first"
          />
        </FormField>

        <FormField
          label="Quantity"
          errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.quantity}
        >
          <Input
            name={`shipments[${shipmentIndex}].lineItems[${idx}].quantity`}
            value={shipments[shipmentIndex]?.lineItems[idx]?.quantity}
            inputMode="numeric"
            onChange={({ detail }) => { setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].quantity`, parseInt(detail.value, 10) || null); }}
          />
        </FormField>
        <FormField
          label="Bonding"
          errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.bonding}
        >
          <Input
            name={`shipments[${shipmentIndex}].lineItems[${idx}].bonding`}
            value={shipments[shipmentIndex]?.lineItems[idx]?.bonding}
            onChange={({ detail }) => { setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].bonding`, detail.value); }}
          />
        </FormField>
        <FormField
          label="Notes"
          errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.notes}
        >
          <Textarea
            name={`shipments[${shipmentIndex}].lineItems[${idx}].notes`}
            value={shipments[shipmentIndex]?.lineItems[idx]?.notes}
            onChange={({ detail }) => { setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].notes`, detail.value); }}
          />
        </FormField>
        <FormField
          label="Kickoff Doc"
          stretch
          errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.kickoffDoc}
        >
          <Input
            name={`shipments[${shipmentIndex}].lineItems[${idx}].kickoffDoc`}
            value={shipments[shipmentIndex]?.lineItems[idx]?.kickoffDoc}
            onChange={({ detail }) => { setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].kickoffDoc`, detail.value); }}
          />
        </FormField>
      </Grid>
      <div className="table_container">
        <Box variant="h5">
          Steps
        </Box>
        {(!item.steps || item.steps.length === 0) && (
          <Box padding={{ top: 's', bottom: 's' }}>
            <Alert type="warning">
              No steps are populated for this line item. Switch the Part ID to another option,
              then re-select this Part ID to populate the steps.
            </Alert>
          </Box>
        )}
        <table className="line_item_table">
          <thead style={{ borderColor: colorBorderDividerDefault }}>
            <tr>
              <th>Type</th>
              <th>Part</th>
              <th>Goal</th>
              <th>Note</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody style={{ borderColor: colorBorderDividerSecondary }}>
            {item.steps?.map((step, stepIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${idx}_step_${stepIdx}`}>
                <td>
                  <FormField
                    errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.steps?.[stepIdx]?.type}
                  >
                    <Select
                      disabled={disableEdit}
                      expandToViewport
                      selectedOption={{ label: step.type, value: step.type }}
                      onChange={({ detail }) => {
                        setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps[${stepIdx}].type`, detail.selectedOption.value);
                        setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps[${stepIdx}].pPartId`, null);
                        setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps[${stepIdx}].pPartDocId`, null);
                      }}
                      options={stepOptions}
                    />
                  </FormField>
                </td>
                <td>
                  <FormField
                    errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.steps?.[stepIdx]?.pPartId}
                    constraintText={getPPartMetadataMessage(step.pPartId)}
                  >
                    <Select
                      disabled={disableEdit}
                      expandToViewport
                      selectedOption={getSelectedPPart(step.pPartId)}
                      onChange={({ detail }) => {
                        setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps[${stepIdx}].pPartId`, detail.selectedOption.value);
                        setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps[${stepIdx}].type`, detail.selectedOption?.stepType || null);
                        setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps[${stepIdx}].pPartDocId`, detail.selectedOption?.pPartDocId || null);
                      }}
                      options={partProducts}
                      empty="No options"
                    />
                  </FormField>
                </td>
                <td style={{ maxWidth: '150px' }}>
                  <FormField
                    errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.steps?.[stepIdx]?.goal}
                  >
                    <Input
                      value={step.goal}
                      inputMode="numeric"
                      onChange={({ detail }) => { setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps[${stepIdx}].goal`, parseInt(detail.value, 10) || null); }}
                    />
                  </FormField>
                </td>
                <td style={{ maxWidth: '350px' }}>
                  <PPartDisplay
                    fieldName="notes"
                    projectDocId={projectDocId}
                    pPartId={step.pPartId}
                    pPartDocId={step.pPartDocId}
                    fallbackMessage={getSelectedPPart(step.pPartId)?.notes || step.note || ''}
                    emptyMessage="This is required. Please include number and type of connectors."
                    showEmptyText={step.type === StepTypes.CONNECTORIZING}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <Button
                    formAction="none"
                    iconName="close"
                    variant="icon"
                    disabled={disableEdit || item.steps?.length === 1}
                    onClick={() => {
                      const updatedSteps = item.steps?.filter((_, i) => i !== stepIdx);
                      setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps`, updatedSteps);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Box padding={{ top: 'm' }}>
          <SpaceBetween direction="horizontal" size="m" alignItems="center" padding>
            <Button
              formAction="none"
              disabled={disableEdit}
              onClick={() => {
                setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].steps`, [
                  ...(item.steps || []),
                  {
                    type: StepTypes.MOLDING, goal: null,
                  },
                ]);
              }}
            >
              Add new step
            </Button>
            {disableEdit && (
            <Box color="text-status-inactive">
              You cannot edit the steps of this line item as it has travelers associated with it.
            </Box>
            )}
          </SpaceBetween>
        </Box>
      </div>
    </SpaceBetween>
  );
}

OnDemandLineItemFields.propTypes = {
  shipmentIndex: PropTypes.number.isRequired,
  idx: PropTypes.number.isRequired,
  disableEdit: PropTypes.bool.isRequired,
};

// ── Main LineItemContent ──────────────────────────────────────────────────────

export default function LineItemContent({ shipmentIndex, lineItemIndex: idx }) {
  const {
    values: { shipments = [], isInternal }, setFieldValue, errors,
  } = useFormikContext();
  const currentLineItems = shipments[shipmentIndex]?.lineItems || [];
  const item = currentLineItems[idx] || {};

  const [disableEdit, setDisableEdit] = useState(true);

  const itemType = item.type || LineItemTypes.ON_DEMAND;

  useEffect(() => {
    if (isInternal && itemType !== LineItemTypes.ON_DEMAND) {
      setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].type`, LineItemTypes.ON_DEMAND);
    }
  }, [isInternal, itemType, setFieldValue, shipmentIndex, idx]);

  useEffect(() => {
    if (item.id) {
      const checkIfTravelerExists = async () => {
        const queryRef = query(
          collection(getFirestore(), 'travelers'),
          where('lineItem', '==', item.id),
          limit(1),
        );
        const querySnapshot = await getDocs(queryRef);

        if (!querySnapshot.empty) {
          setDisableEdit(true);
        } else {
          setDisableEdit(false);
        }
      };
      checkIfTravelerExists();
    } else {
      setDisableEdit(false);
    }
  }, [item?.id]);

  return (
    <Container
      header={(
        <Header
          // the frozen customer-facing line from the quote PDF / checkout
          description={item.description}
          actions={idx !== 0 && (
          <Button
            type="button"
            disabled={disableEdit}
            onClick={() => {
              const updatedLineItems = currentLineItems.filter((_, i) => i !== idx);
              setFieldValue(`shipments[${shipmentIndex}].lineItems`, updatedLineItems);
            }}
          >
            Delete LineItem
          </Button>
          )}
        >
          <div id={`item_${shipmentIndex}_${idx}`}>
            {`Line Item ${idx + 1}`}
          </div>
        </Header>
        )}
    >
      <SpaceBetween size="s">
        {!isInternal && (
          <FormField
            label="Line Item Type"
            errorText={errors?.shipments?.[shipmentIndex]?.lineItems?.[idx]?.type}
          >
            <Select
              disabled={disableEdit}
              selectedOption={LINE_ITEM_TYPES_OPTIONS.find((t) => t.value === itemType) || LINE_ITEM_TYPES_OPTIONS[0]}
              onChange={({ detail }) => {
                setFieldValue(`shipments[${shipmentIndex}].lineItems[${idx}].type`, detail.selectedOption.value);
              }}
              options={LINE_ITEM_TYPES_OPTIONS}
            />
          </FormField>
        )}

        {/* Conditionally render on_demand or product fields */}
        {!isInternal && itemType === LineItemTypes.PRODUCT ? (
          <ProductLineItemFields
            shipmentIndex={shipmentIndex}
            idx={idx}
            disableEdit={disableEdit}
          />
        ) : (
          <OnDemandLineItemFields
            shipmentIndex={shipmentIndex}
            idx={idx}
            disableEdit={disableEdit}
          />
        )}
      </SpaceBetween>
    </Container>
  );
}

LineItemContent.propTypes = {
  shipmentIndex: PropTypes.number.isRequired,
  lineItemIndex: PropTypes.number.isRequired,
};
